import React, {useEffect, useRef, useState} from 'react';
import {Button, Dialog, DialogContent, DialogTitle, TextField, Typography,} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import {AudioChannel} from "../../services/audio/audioChannel";
import {showMessage} from "../../utils/snackbar";
import {getMicStream} from "../../utils/mic";


export interface AIDialogProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    onChange: (value: string) => void;
    onConfirm: () => void;
    confirmButtonText?: string;
    cancelButtonText?: string;
    allowCmdEnter?: boolean;
    /** The language code you want to use for speech‑to‑text (e.g. 'nl', 'en'). */
    language: string;
}

export const AIDialog = ({
                             open,
                             onClose,
                             title = 'Wat kan ik voor je aanpassen?',
                             onChange,
                             onConfirm,
                             allowCmdEnter = true,
                             language,
                         }: AIDialogProps) => {
    const [isRecording, setIsRecording] = useState(false);
    const [completed, setCompleted] = useState('');
    const [uncompleted, setUncompleted] = useState('');
    const audioChannel = useRef<AudioChannel | null>(null);

    useEffect(() => {
        if (open) {
            setCompleted('');
            setUncompleted('');
        }
    }, [open]);

    useEffect(() => {
        onChange(completed + ' ' + uncompleted);
    }, [completed, uncompleted, onChange]);

    const handleNewTranscript = (transcript: string, completed: boolean) => {
        if (completed) {
            setCompleted(prev => (prev ? prev + '\n' : '') + transcript);
            setUncompleted('');
        } else {
            setUncompleted(transcript);
        }
    };

    const handleStartRecording = async () => {
        try {
            setIsRecording(true);

            audioChannel.current = new AudioChannel(language);
            audioChannel.current.transcriptHandler = handleNewTranscript;
            audioChannel.current.errorHandler = (e) => handleStopRecording();

            await audioChannel.current.connect();
            await audioChannel.current.setAudioInput(getMicStream);

        } catch (error: any) {
            if (error.message === 'MIC_BLOCKED' || error.name === 'NotAllowedError') {
                showMessage("Microfoon toegang geweigerd. Controleer je browserinstellingen.", "error");
            }
            await handleStopRecording();
        }
    };

    const handleStopRecording = async () => {
        await audioChannel.current?.stop();
        setIsRecording(false);
    };

    const handleMicToggle = async () => {
        if (isRecording) await handleStopRecording();
        else await handleStartRecording();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (allowCmdEnter && (e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleConfirm();
        }
    };

    const handleConfirm = () => {
        if (isRecording) handleStopRecording();
        onConfirm();
        onClose();
    };

    const handleDialogClose = () => {
        if (isRecording) handleStopRecording();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            slotProps={{
                paper: {
                    style: {
                        minWidth: 400,
                        position: 'absolute',
                        margin: 0,
                        padding: '16px',
                        borderRadius: '16px',
                        borderColor: 'primary',
                    },
                },
            }}
        >
            <DialogTitle sx={{p: 0, m: 0, mt: '8px', mb: '2px', lineHeight: 1}}>
                {title}
            </DialogTitle>

            <DialogContent sx={{p: 0, m: 0, position: 'relative'}}>
                <TextField
                    fullWidth
                    multiline
                    autoFocus
                    minRows={4}
                    value={completed}
                    onChange={(e) => setCompleted(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Geef aan wat je wilt wijzigen"
                    sx={{
                        '& textarea': {
                            paddingBottom: '18px'
                        }
                    }}
                />
                <Typography
                    variant="body1"
                    component="span"
                    sx={{
                        position: 'absolute',
                        whiteSpace: "nowrap",
                        color: '#d3d3d3',
                        left: 14,
                        bottom: 9,
                        display: 'inline-block',
                        width: '250px',
                        overflow: 'hidden !important',
                        textOverflow: 'ellipsis'
                    }}>
                    {uncompleted}
                </Typography>
                {/* Microphone toggle */}
                <Button
                    onClick={handleMicToggle}
                    sx={{
                        width: 32,
                        height: 32,
                        position: 'absolute',
                        right: completed ? 32 + 9 + 9 : 9,
                        bottom: 9,
                        minWidth: 'auto',
                        padding: '6px',
                        backgroundColor: 'grey.500',
                        color: 'grey.100',
                        borderRadius: '50%',
                        '&:hover': {backgroundColor: 'grey.700'},
                        transition: 'background-color 0.2s',
                    }}
                >
                    {isRecording ? <MicOffIcon fontSize="small"/> : <MicIcon fontSize="small"/>}
                </Button>
                {completed && <Button
                    onClick={handleConfirm}
                    sx={{
                        width: 32,
                        height: 32,
                        position: 'absolute',
                        right: 9,
                        bottom: 9,
                        minWidth: 'auto',
                        padding: '6px',
                        backgroundColor: 'grey.500',
                        color: 'grey.100',
                        borderRadius: '50%',
                        '&:hover': {backgroundColor: 'grey.700'},
                        transition: 'background-color 0.2s',
                    }}
                >
                    <ArrowForwardIcon fontSize="small"/>
                </Button>
                }
            </DialogContent>
        </Dialog>
    );
};
